const EV = {
    GOTO: 'slider:goto',
    MOVE: 'slider:move',
    CHANGE: 'slider:change',
    READY: 'slider:ready',
};
if (!customElements.get('new-slider')) {
    class NewSlider extends HTMLElement {
        scroller;
        slides = [];
        abort;
        io;
        _index = 0;
        // rAF guard for IO
        ioTickPending = false;
        get index() {
            return this._index;
        }
        get count() {
            return this.slides.length;
        }
        connectedCallback() {
            this.abort = new AbortController();
            const scroller = this.querySelector('[id^="Slider-"]');
            if (!scroller)
                return;
            this.scroller = scroller;
            this.slides = Array.from(this.scroller.querySelectorAll('[id^="Slide-"]'));
            if (!this.slides.length)
                return;
            // Commands
            this.addEventListener(EV.GOTO, this.onGoto, { signal: this.abort.signal });
            this.addEventListener(EV.MOVE, this.onMove, { signal: this.abort.signal });
            // Snap request from children (e.g., scrollbar)
            this.addEventListener('slider:snap', () => this.snapToNearest(true, 'start'), {
                signal: this.abort.signal,
                passive: true,
            });
            // Refresh request (e.g., after slider-loop adds clones)
            this.addEventListener('slider:refresh', () => this.refresh(), {
                signal: this.abort.signal,
            });
            // Observe visibility; throttle with rAF flag
            this.io = new IntersectionObserver(() => this.scheduleIOTick(), {
                root: this.scroller,
                threshold: [0.01, 0.6, 0.999],
                rootMargin: '0px 1px',
            });
            requestAnimationFrame(() => this.slides.forEach((el) => this.io.observe(el)));
            this.goTo(this._index, false);
            this.dispatchEvent(new CustomEvent(EV.READY, {
                bubbles: true,
                detail: { slides: this.count, index: this._index },
            }));
        }
        disconnectedCallback() {
            this.abort?.abort();
            this.io?.disconnect();
        }
        // — Public API —
        goTo(index, animate = true) {
            if (!this.scroller || !this.count)
                return;
            const i = Math.max(0, Math.min(index, this.count - 1));
            this._index = i;
            // offsetLeft is measured from the offsetParent's padding edge, so on a scroller
            // with padding-left it is non-zero even for the first slide and would nudge
            // index 0 off the scroll origin (scroll-snap then settles on whichever snap
            // point is nearest). Scroll by the distance from the first slide instead,
            // which is 0 at index 0 by construction.
            const first = this.slides[0];
            const target = this.slides[i];
            if (!first || !target)
                return;
            const left = target.offsetLeft - first.offsetLeft;
            if (!animate) {
                // Override CSS scroll-behavior: smooth to ensure instant jump
                this.scroller.style.scrollBehavior = 'auto';
                this.scroller.scrollLeft = left;
                // Restore on next frame so smooth scrolling works again
                requestAnimationFrame(() => {
                    this.scroller.style.scrollBehavior = '';
                });
            }
            else {
                this.scroller.scrollTo({
                    left,
                    behavior: 'smooth',
                });
            }
        }
        moveBy(delta, animate = true) {
            this.goTo(this._index + delta, animate);
        }
        nearestIndex(mode = 'start') {
            if (!this.scroller || !this.count)
                return this._index;
            const first = this.slides[0];
            if (!first)
                return this._index;
            // Same origin as goTo(): scrollLeft counts from the scroll origin, offsetLeft from the offsetParent's padding edge.
            const origin = first.offsetLeft;
            const sl = this.scroller.scrollLeft;
            const target = mode === 'start' ? sl : sl + this.scroller.clientWidth / 2;
            let best = this._index;
            let bestDist = Number.POSITIVE_INFINITY;
            for (let i = 0; i < this.slides.length; i++) {
                const el = this.slides[i];
                const start = el.offsetLeft - origin;
                const center = start + el.offsetWidth / 2;
                const ref = mode === 'start' ? start : center;
                const d = Math.abs(ref - target);
                if (d < bestDist) {
                    bestDist = d;
                    best = i;
                }
            }
            return best;
        }
        snapToNearest(animate = true, mode = 'start') {
            this.goTo(this.nearestIndex(mode), animate);
        }
        refresh() {
            if (!this.scroller)
                return;
            this.io?.disconnect();
            this.slides = Array.from(this.scroller.querySelectorAll('[id^="Slide-"]'));
            if (!this.slides.length)
                return;
            this.io = new IntersectionObserver(() => this.scheduleIOTick(), {
                root: this.scroller,
                threshold: [0.01, 0.6, 0.999],
                rootMargin: '0px 1px',
            });
            requestAnimationFrame(() => this.slides.forEach((el) => this.io.observe(el)));
        }
        // — Private —
        onGoto = (e) => {
            const { index, animate } = e.detail ?? {};
            if (Number.isInteger(index))
                this.goTo(index, animate ?? true);
        };
        onMove = (e) => {
            const { delta } = e.detail ?? {};
            if (typeof delta === 'number' && delta)
                this.moveBy(delta);
        };
        scheduleIOTick() {
            if (this.ioTickPending)
                return;
            this.ioTickPending = true;
            requestAnimationFrame(() => {
                this.ioTickPending = false;
                this.updateActiveFromVisibility();
            });
        }
        visibleSlides() {
            const root = this.scroller.getBoundingClientRect();
            const centerX = (root.left + root.right) / 2;
            return this.slides
                .map((el, index) => {
                const r = el.getBoundingClientRect();
                const overlapX = Math.min(r.right, root.right) - Math.max(r.left, root.left);
                if (overlapX <= 0)
                    return null;
                const width = Math.max(1, r.width);
                return {
                    index,
                    left: r.left - root.left,
                    width,
                    overlapX,
                    ratio: Math.min(1, Math.max(0, overlapX / width)),
                    centerOffset: (r.left + r.right) / 2 - centerX,
                    fully: overlapX >= width - 1,
                };
            })
                .filter((x) => x !== null);
        }
        pickNextIndex(visible, policy) {
            if (!visible.length)
                return this._index;
            if (policy === 'first-visible') {
                return (visible.find((v) => v.fully) ?? visible[0]).index;
            }
            if (policy === 'most-visible') {
                const EPS = 0.01;
                let best = visible[0];
                for (const v of visible) {
                    const better = v.ratio > best.ratio + EPS;
                    const nearTie = Math.abs(v.ratio - best.ratio) <= EPS && Math.abs(v.centerOffset) < Math.abs(best.centerOffset);
                    if (better || nearTie)
                        best = v;
                }
                return best.index;
            }
            // center-closest
            let best = visible[0];
            for (const v of visible)
                if (Math.abs(v.centerOffset) < Math.abs(best.centerOffset))
                    best = v;
            return best.index;
        }
        updateActiveFromVisibility() {
            if (!this.scroller || !this.slides.length)
                return;
            const policy = this.getAttribute('data-active-policy') || 'center-closest';
            const visible = this.visibleSlides();
            if (!visible.length)
                return;
            const next = this.pickNextIndex(visible, policy);
            if (next === this._index)
                return;
            this._index = next;
            this.dispatchEvent(new CustomEvent(EV.CHANGE, {
                bubbles: true,
                detail: { index: this._index, visible: visible.map((v) => v.index) },
            }));
        }
    }
    customElements.define('new-slider', NewSlider);
}
if (!customElements.get('slider-scrollbar')) {
    class SliderScrollbar extends HTMLElement {
        track;
        thumb;
        scroller;
        host = null;
        abort;
        ro;
        dragStartX = 0;
        dragStartScrollLeft = 0;
        maxScroll = 0;
        trackWidth = 0;
        thumbWidth = 0;
        thumbMaxX = 0;
        // rAF guard for scroll sync
        syncPending = false;
        connectedCallback() {
            this.abort = new AbortController();
            this.track = this.querySelector('[data-ref="track"]');
            this.thumb = this.querySelector('[data-ref="thumb"]');
            this.host = this.closest('new-slider');
            this.scroller = this.host?.querySelector('[id^="Slider-"]') || null;
            if (!this.track || !this.thumb || !this.host || !this.scroller)
                return;
            this.scroller.addEventListener('scroll', () => this.scheduleSync(), {
                signal: this.abort.signal,
                passive: true,
            });
            this.track.addEventListener('pointerdown', this.onTrackPointerDown, { signal: this.abort.signal });
            this.thumb.addEventListener('pointerdown', this.onThumbPointerDown, { signal: this.abort.signal });
            this.ro = new ResizeObserver(() => {
                this.measure();
                this.syncNow();
            });
            this.ro.observe(this.scroller);
            this.ro.observe(this.track);
            this.measure();
            this.syncNow();
        }
        disconnectedCallback() {
            this.abort?.abort();
            this.ro?.disconnect();
        }
        measure() {
            const scrollW = this.scroller.scrollWidth;
            const clientW = this.scroller.clientWidth;
            this.maxScroll = Math.max(0, scrollW - clientW);
            this.trackWidth = this.track.clientWidth;
            const ratio = scrollW > 0 ? clientW / scrollW : 1;
            this.thumbWidth = Math.max(8, Math.round(this.trackWidth * ratio));
            this.thumb.style.width = `${this.thumbWidth}px`;
            this.thumbMaxX = Math.max(0, this.trackWidth - this.thumbWidth);
        }
        scheduleSync() {
            if (this.syncPending)
                return;
            this.syncPending = true;
            requestAnimationFrame(() => {
                this.syncPending = false;
                this.syncNow();
            });
        }
        syncNow() {
            if (this.maxScroll <= 0 || this.thumbMaxX <= 0) {
                this.thumb.style.transform = 'translateX(0)';
                return;
            }
            const x = (this.scroller.scrollLeft / this.maxScroll) * this.thumbMaxX;
            this.thumb.style.transform = `translateX(${x}px)`;
        }
        onTrackPointerDown = (e) => {
            const rect = this.track.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const x = Math.max(0, Math.min(clickX - this.thumbWidth / 2, this.thumbMaxX));
            const ratio = this.thumbMaxX > 0 ? x / this.thumbMaxX : 0;
            this.scroller.scrollTo({ left: ratio * this.maxScroll, behavior: 'smooth' });
        };
        onThumbPointerDown = (e) => {
            this.thumb.setPointerCapture(e.pointerId);
            this.dragStartX = e.clientX;
            this.dragStartScrollLeft = this.scroller.scrollLeft;
            this.thumb.classList.add('is-dragging');
            this.thumb.addEventListener('pointermove', this.onThumbPointerMove, { signal: this.abort.signal });
            this.thumb.addEventListener('pointerup', this.onThumbPointerUp, { signal: this.abort.signal, passive: true });
            this.thumb.addEventListener('pointercancel', this.onThumbPointerUp, { signal: this.abort.signal, passive: true });
        };
        onThumbPointerMove = (e) => {
            const dx = e.clientX - this.dragStartX;
            const delta = this.thumbMaxX > 0 ? (dx / this.thumbMaxX) * this.maxScroll : 0;
            const left = Math.max(0, Math.min(this.dragStartScrollLeft + delta, this.maxScroll));
            this.scroller.scrollTo({ left, behavior: 'auto' });
            this.syncNow();
        };
        onThumbPointerUp = (e) => {
            this.thumb.releasePointerCapture(e.pointerId);
            this.thumb.classList.remove('is-dragging');
            // Ask host to snap via event (no casts/type guards)
            this.host?.dispatchEvent(new CustomEvent('slider:snap', { bubbles: true }));
        };
    }
    customElements.define('slider-scrollbar', SliderScrollbar);
}
if (!customElements.get('slider-control')) {
    class SliderControl extends HTMLElement {
        abort;
        connectedCallback() {
            this.abort = new AbortController();
            this.addEventListener('click', this.onClick, { signal: this.abort.signal });
        }
        disconnectedCallback() {
            this.abort?.abort();
        }
        onClick = (e) => {
            e.preventDefault();
            const cmd = this.dataset.command?.trim();
            if (!cmd)
                return;
            if (cmd.startsWith('+') || cmd.startsWith('-')) {
                const delta = Number(cmd);
                if (!Number.isNaN(delta) && delta) {
                    this.dispatchEvent(new CustomEvent(EV.MOVE, { bubbles: true, composed: true, detail: { delta } }));
                }
                return;
            }
            const index = Number(cmd);
            if (Number.isInteger(index)) {
                this.dispatchEvent(new CustomEvent(EV.GOTO, { bubbles: true, composed: true, detail: { index } }));
            }
        };
    }
    customElements.define('slider-control', SliderControl);
}
if (!customElements.get('slider-loop')) {
    class SliderLoop extends HTMLElement {
        host = null;
        scroller = null;
        io = null;
        abort = new AbortController();
        cloneStart = null;
        cloneEnd = null;
        realCount = 0;
        connectedCallback() {
            this.abort = new AbortController();
            this.host = this.closest('new-slider');
            this.scroller = this.host?.querySelector('[id^="Slider-"]') ?? null;
            if (!this.host || !this.scroller)
                return;
            const slides = Array.from(this.scroller.querySelectorAll('[id^="Slide-"]'));
            if (slides.length < 2)
                return;
            this.realCount = slides.length;
            const firstSlide = slides[0];
            const lastSlide = slides[slides.length - 1];
            // Clone first → append at end, clone last → prepend at start
            this.cloneEnd = firstSlide.cloneNode(true);
            this.cloneStart = lastSlide.cloneNode(true);
            this.cloneEnd.id = `${firstSlide.id}-clone-end`;
            this.cloneStart.id = `${lastSlide.id}-clone-start`;
            this.cloneEnd.setAttribute('aria-hidden', 'true');
            this.cloneStart.setAttribute('aria-hidden', 'true');
            this.scroller.appendChild(this.cloneEnd);
            this.scroller.insertBefore(this.cloneStart, firstSlide);
            // Tell new-slider to re-query slides (now includes clones)
            this.host.dispatchEvent(new CustomEvent('slider:refresh'));
            // Position on first real slide (index 1, after prepended clone)
            this.host.dispatchEvent(new CustomEvent(EV.GOTO, { detail: { index: 1, animate: false } }));
            // IntersectionObserver: jump when a clone becomes fully visible
            this.io = new IntersectionObserver((entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting)
                        continue;
                    if (entry.target === this.cloneStart) {
                        // Clone of last at start → jump to real last slide
                        requestAnimationFrame(() => {
                            this.host?.dispatchEvent(new CustomEvent(EV.GOTO, { detail: { index: this.realCount, animate: false } }));
                        });
                    }
                    else if (entry.target === this.cloneEnd) {
                        // Clone of first at end → jump to real first slide
                        requestAnimationFrame(() => {
                            this.host?.dispatchEvent(new CustomEvent(EV.GOTO, { detail: { index: 1, animate: false } }));
                        });
                    }
                }
            }, { root: this.scroller, threshold: 0.999 });
            this.io.observe(this.cloneStart);
            this.io.observe(this.cloneEnd);
        }
        disconnectedCallback() {
            this.abort.abort();
            this.io?.disconnect();
            // Remove clones from DOM
            this.cloneStart?.remove();
            this.cloneEnd?.remove();
        }
    }
    customElements.define('slider-loop', SliderLoop);
}
if (!customElements.get('slider-dots')) {
    class SliderDots extends HTMLElement {
        abort = new AbortController();
        dots = [];
        connectedCallback() {
            this.abort = new AbortController();
            this.dots = Array.from(this.querySelectorAll('button'));
            const host = this.closest('new-slider');
            if (!host)
                return;
            // Determine index offset (1 if inside a slider-loop, 0 otherwise)
            const loop = this.closest('slider-loop') ?? host.querySelector('slider-loop');
            const offset = loop ? 1 : 0;
            const realCount = this.dots.length;
            // Click dot → navigate to slide
            this.dots.forEach((dot, i) => {
                dot.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.dispatchEvent(new CustomEvent(EV.GOTO, {
                        bubbles: true,
                        composed: true,
                        detail: { index: i + offset },
                    }));
                }, { signal: this.abort.signal });
            });
            // Update active dot on slide change
            host.addEventListener(EV.CHANGE, ((e) => {
                let idx = e.detail.index - offset;
                if (idx < 0)
                    idx = realCount - 1;
                if (idx >= realCount)
                    idx = 0;
                this.setActive(idx);
            }), { signal: this.abort.signal });
            // Set initial active state
            host.addEventListener(EV.READY, ((e) => {
                let idx = e.detail.index - offset;
                if (idx < 0)
                    idx = realCount - 1;
                if (idx >= realCount)
                    idx = 0;
                this.setActive(idx);
            }), { signal: this.abort.signal });
            this.setActive(0);
        }
        disconnectedCallback() {
            this.abort.abort();
        }
        setActive(index) {
            this.dots.forEach((dot, i) => {
                const isActive = i === index;
                dot.classList.toggle('slider-counter__link--active', isActive);
                if (isActive) {
                    dot.setAttribute('aria-current', 'true');
                }
                else {
                    dot.removeAttribute('aria-current');
                }
            });
        }
    }
    customElements.define('slider-dots', SliderDots);
}
